/**
 * @author Mukesh kumar bairwa <mukesh.bairwa@314ecorp.com>
 * @description Query Builder workflow automation
 */

import '@react-awesome-query-builder/antd/css/styles.css';
import React, { ReactNode } from 'react';
import _ from 'lodash';
import { Builder, BuilderProps, Config, Fields, ImmutableTree, JsonTree, Query, Utils } from '@react-awesome-query-builder/antd';

import { getConfig } from 'lib/SearchQueryBuilder/config/queryConfig';
import { getDefaultQuery } from 'utils/advancedFilterUtils';
import '../raqb.css';

interface IProps {
	dateFormat: string;
	value: JsonTree;
	fields: Fields;
	onChange: (value: JsonTree) => void;
}

interface IState {
	value: ImmutableTree;
	config: Config;
	fields: Fields;
}
const isEmptyQuery = (query: JsonTree): boolean => {
	return _.isEmpty(query) || _.isEmpty(query.children1);
};

class QueryBuilder extends React.PureComponent<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		const { fields, dateFormat } = props;
		const config = getConfig(fields, dateFormat);
		const initialQuery = getDefaultQuery(config);
		this.state = {
			config,
			value: Utils.loadTree(isEmptyQuery(this.props.value) ? initialQuery : this.props.value),
			fields,
		};
	}

	componentDidUpdate(prevProps: IProps): void {
		const { value, fields, dateFormat } = this.props;

		if (value !== prevProps.value && !_.isEqual(value, Utils.getTree(this.state.value))) {
			const config = getConfig(fields, dateFormat);
			const initialQuery = getDefaultQuery(config);
			this.setState({
				value: Utils.checkTree(Utils.loadTree(isEmptyQuery(value) ? initialQuery : value), config),
			});
		}
	}

	handleChange = (immutableTree: ImmutableTree, config: Config): void => {
		const query = Utils.getTree(immutableTree);
		// If query is null or empty, reset to default query
		if (_.isEmpty((query?.children1?.[0] as JsonTree)?.children1)) {
			const defaultQuery = getDefaultQuery(config);
			const defaultImmutableTree = Utils.loadTree(defaultQuery);
			this.setState(
				{
					value: defaultImmutableTree,
					config,
				},
				() => {
					this.props.onChange?.(defaultQuery);
				},
			);
			return;
		}

		this.setState(
			{
				value: immutableTree,
				config,
			},
			() => {
				this.props.onChange?.(query);
			},
		);
	};

	renderBuilder = (builderProps: BuilderProps): AnyType => (
		<div className='query-builder-container'>
			<div className='query-builder'>
				<Builder {...builderProps} />
			</div>
		</div>
	);

	render(): ReactNode {
		const { value, config, fields } = this.state;
		return <Query {...config} value={value} renderBuilder={this.renderBuilder} onChange={this.handleChange} fields={fields} />;
	}
}

export default QueryBuilder;
